import { createContext, useState, ReactNode, useCallback } from 'react';
import { decodeJwt, JWTPayload } from 'jose';
import { JWT_TOKEN_KEY, USERNAME_KEY } from '../config/constants';
import useLocalStorage from '../hooks/useLocalStorage';
import { addBackendPrefix } from '../utils/backendPrefix';

interface AuthContextType {
  username: string | undefined;
  isLoggedIn: boolean;
  register: (username: string, nickname: string, password: string) => Promise<Response>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  token: string | undefined;
  refreshAccessToken: () => Promise<string | undefined>;
  checkTokenExpiry: (token: string | undefined) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [username, setUsername] = useLocalStorage<string | undefined>(USERNAME_KEY, undefined);
  const [token, setToken] = useLocalStorage<string | undefined>(JWT_TOKEN_KEY, undefined);

  // only do initial check for valid token when context is created
  // we're not going to log people out while they have a session
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    () => {
      if (!token) return false;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTimeInSeconds = Date.now() / 1000;
        const isExpired = currentTimeInSeconds >= payload.exp; // Compare current time with exp
        return !isExpired;
      } catch (e) {
        return false;
      }
    }
  );

  async function register(username: string, nickname: string, password: string) {
    return await fetch(addBackendPrefix('/register'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, nickname, password })
    });
  }

  async function login(username: string, password: string) {
    const response = await fetch(addBackendPrefix('/login'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) {
      const errorText = await response.text();
      if (response.status === 401)
        throw new Error(errorText); // we were expecting this
      else
        throw new Error(`HTTP error: ${response.status} (${errorText})`);
    }

    const data = await response.json();

    if (data?.token != null) {
      setToken(data.token);
      setIsLoggedIn(true);
      setUsername(username);
    } else {
      throw new Error('Missing token in login response');
    }
  }

  async function logout() {
    setToken(undefined);
    setIsLoggedIn(false);
    setUsername(undefined);

    // log out server-side too
    const response = await fetch(addBackendPrefix('/logout'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error: ${response.status} (${errorText})`);
    }

    //    window.location.reload(); // force all state to refresh
  }

  // Function to refresh the access token
  // (will be used by Axios to access protected API resources)
  const refreshAccessToken = useCallback(
    async () => {
      const response = await fetch(addBackendPrefix('/token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        return data.token;
      } else {
        throw new Error('Failed to refresh token');
      }
    }, [setToken]
  );

  const checkTokenExpiry = useCallback(
    (token: string | undefined) => {
      if (!token) return true;
      const decodedToken = decodeJwt(token);
      const now = Date.now().valueOf() / 1000;
      return isJWTPayloadWithExp(decodedToken) && decodedToken.exp < now;
    }, []
  );

  return (
    <AuthContext.Provider
      value={{
        username,
        isLoggedIn,
        register,
        login,
        logout,
        token,
        refreshAccessToken,
        checkTokenExpiry
      }}>
      {children}
    </AuthContext.Provider>
  );
}

function isJWTPayloadWithExp(payload: JWTPayload | string | null): payload is JWTPayload & { exp: number } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    !Array.isArray(payload) &&
    typeof (payload as JWTPayload).exp === 'number'
  );
}
